/**
 * Telegram Executive Dashboard
 * Designed for C-Suite executives who demand excellence
 */

import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Users, DollarSign, Target, Activity, Award,
  ArrowUp, ArrowDown, ChevronRight, Globe, Zap, Shield
} from 'lucide-react';

interface ExecutiveMetric {
  title: string;
  value: string;
  change: number;
  trend: 'up' | 'down' | 'neutral';
  subtitle: string;
  impact: string;
}

interface RevenueStream {
  source: string;
  amount: string;
  growth: number;
  percentage: number;
}

export default function TelegramExecutiveDashboard() {
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month' | 'quarter'>('today');
  const [animatedValues, setAnimatedValues] = useState({
    revenue: 0,
    customers: 0,
    satisfaction: 0,
    efficiency: 0
  });

  useEffect(() => {
    // Smooth number animations
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    
    const targets = {
      revenue: 247000,
      customers: 8439,
      satisfaction: 94,
      efficiency: 87
    };
    
    let step = 0;
    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      setAnimatedValues({
        revenue: Math.round(targets.revenue * easeOut),
        customers: Math.round(targets.customers * easeOut),
        satisfaction: Math.round(targets.satisfaction * easeOut),
        efficiency: Math.round(targets.efficiency * easeOut)
      });
      
      if (step >= steps) clearInterval(timer);
    }, interval);
    
    return () => clearInterval(timer);
  }, [selectedPeriod]);

  const metrics: ExecutiveMetric[] = [
    {
      title: 'Revenue Impact',
      value: `$${animatedValues.revenue.toLocaleString()}`,
      change: 23,
      trend: 'up',
      subtitle: 'Automated conversations',
      impact: '+$47K from automation'
    },
    {
      title: 'Active Customers',
      value: animatedValues.customers.toLocaleString(),
      change: 18,
      trend: 'up',
      subtitle: '24/7 engagement',
      impact: '1,247 new this period'
    },
    {
      title: 'Satisfaction Score',
      value: `${animatedValues.satisfaction}%`,
      change: 5,
      trend: 'up',
      subtitle: 'Customer happiness',
      impact: 'Industry leading'
    },
    {
      title: 'Efficiency Rate',
      value: `${animatedValues.efficiency}%`,
      change: 12,
      trend: 'up',
      subtitle: 'Automation success',
      impact: '3.2x faster resolution'
    }
  ];

  const revenueStreams: RevenueStream[] = [
    { source: 'Automated Sales', amount: '$142,000', growth: 34, percentage: 58 },
    { source: 'Support Savings', amount: '$67,000', growth: 22, percentage: 27 },
    { source: 'Lead Generation', amount: '$38,000', growth: 45, percentage: 15 }
  ];

  const insights = [
    {
      type: 'opportunity',
      message: 'Enable appointment booking to capture $24K in missed revenue',
      action: 'Activate Now'
    },
    {
      type: 'success',
      message: 'Response time improved by 94% this week',
      action: 'View Report'
    },
    {
      type: 'alert',
      message: '312 high-value leads require follow-up',
      action: 'Assign Team'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-900 to-black text-white">
      {/* Premium Header */}
      <div className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-light tracking-tight">Executive Command Center</h1>
              <p className="text-gray-400 mt-1">Real-time business intelligence</p>
            </div>
            
            {/* Period Selector - Minimalist Design */}
            <div className="flex bg-gray-800/50 rounded-full p-1 backdrop-blur">
              {(['today', 'week', 'month', 'quarter'] as const).map(period => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedPeriod === period 
                      ? 'bg-white text-black' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Key Metrics - Large, Bold, Important */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.map((metric, index) => (
            <div 
              key={index}
              className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur rounded-2xl p-6 border border-gray-800 hover:border-gray-700 transition-all duration-300 group"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-gray-400 text-sm font-medium">{metric.title}</p>
                  <p className="text-4xl font-light mt-2 tracking-tight">{metric.value}</p>
                </div>
                <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                  metric.trend === 'up' 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {metric.trend === 'up' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                  {Math.abs(metric.change)}%
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-gray-500 text-sm">{metric.subtitle}</p>
                <p className="text-gray-400 text-sm font-medium group-hover:text-white transition-colors">
                  {metric.impact}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Revenue Intelligence */}
        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Revenue Breakdown */}
          <div className="lg:col-span-2 bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur rounded-2xl p-8 border border-gray-800">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-light">Revenue Streams</h2>
              <span className="text-green-400 text-sm font-medium">+28% overall</span>
            </div>
            
            <div className="space-y-6">
              {revenueStreams.map((stream, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-300">{stream.source}</span>
                    <div className="flex items-center gap-4">
                      <span className="text-2xl font-light">{stream.amount}</span>
                      <span className={`text-sm font-medium ${
                        stream.growth > 0 ? 'text-green-400' : 'text-red-400'
                      }`}>
                        +{stream.growth}%
                      </span>
                    </div>
                  </div>
                  <div className="relative h-3 bg-gray-800 rounded-full overflow-hidden">
                    <div 
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-1000"
                      style={{ width: `${stream.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-8 pt-6 border-t border-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Projected Quarter End</span>
                <span className="text-3xl font-light">$892,000</span>
              </div>
            </div>
          </div>
          
          {/* AI Insights */}
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur rounded-2xl p-8 border border-gray-800">
            <h2 className="text-xl font-light mb-6">AI Insights</h2>
            
            <div className="space-y-4">
              {insights.map((insight, index) => (
                <div 
                  key={index}
                  className={`p-4 rounded-xl border transition-all duration-300 hover:scale-105 cursor-pointer ${
                    insight.type === 'opportunity' 
                      ? 'bg-blue-500/10 border-blue-500/30 hover:border-blue-400' 
                      : insight.type === 'success'
                      ? 'bg-green-500/10 border-green-500/30 hover:border-green-400'
                      : 'bg-yellow-500/10 border-yellow-500/30 hover:border-yellow-400'
                  }`}
                >
                  <p className="text-sm text-gray-300 mb-3">{insight.message}</p>
                  <button className="flex items-center gap-2 text-sm font-medium text-white hover:gap-3 transition-all">
                    {insight.action}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Performance Matrix */}
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur rounded-2xl p-8 border border-gray-800">
          <h2 className="text-xl font-light mb-6">Performance Matrix</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { label: 'Response Time', value: '1.2s', target: '< 2s', status: 'optimal' },
              { label: 'Automation Rate', value: '87%', target: '85%', status: 'optimal' },
              { label: 'Resolution Rate', value: '94%', target: '90%', status: 'optimal' },
              { label: 'Cost per Interaction', value: '$0.12', target: '< $0.50', status: 'optimal' }
            ].map((metric, index) => (
              <div key={index} className="text-center">
                <div className="relative inline-flex items-center justify-center w-32 h-32">
                  <svg className="absolute inset-0 transform -rotate-90">
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      className="text-gray-800"
                    />
                    <circle
                      cx="64"
                      cy="64"
                      r="56"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${2 * Math.PI * 56}`}
                      strokeDashoffset={`${2 * Math.PI * 56 * 0.13}`}
                      className="text-green-500 transition-all duration-1000"
                    />
                  </svg>
                  <span className="text-2xl font-light">{metric.value}</span>
                </div>
                <p className="text-gray-400 text-sm mt-3">{metric.label}</p>
                <p className="text-gray-500 text-xs mt-1">Target: {metric.target}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Status Bar */}
        <div className="mt-8 flex items-center justify-between text-sm text-gray-500">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-green-500" />
              All systems operational
            </span>
            <span className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-blue-500" />
              Enterprise security active
            </span>
            <span className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-purple-500" />
              Global coverage enabled
            </span>
          </div>
          <span>Last updated: {new Date().toLocaleTimeString()}</span>
        </div>
      </div>
    </div>
  );
}